// The leased watch every family that lets a caller follow a change streams in
// (02-DESIGN/06-configuration/01-the-configuration-service.md § the leased
// watch; decision 0012): a snapshot — one content frame per entry, then an
// empty progress frame that says the snapshot is complete — then one content
// frame per change, ended by the service with done and the last revision just
// inside the import's response threshold, and reopened here from that
// revision so that no reader sees the seam — and reopened at the connection's
// own reconnect, from a fresh snapshot, because the reply subject the lease
// was answered on belonged to the connection that dropped. The frames are the frame grammar;
// a family instantiates the watch with its three headers and its entry
// decoding and defines nothing of its own. This is the client half only — the
// configuration watch, the guardrails rule watch and the state watch open it;
// the server half is a service's.
import { headers as newHeaders, } from "@nats-io/nats-core";
import * as frame from "./frame.js";
import { audienceOf, CARRIER_HEADER } from "./identity.js";
/** The values of the snapshot header. */
export const SNAPSHOT_TRUE = "true";
export const SNAPSHOT_DONE = "done";
/**
 * Kind is what an event is: the four events, in the order a caller sees them
 * — a snapshot begins, its entries, its completion, then changes — and a
 * snapshot again only when the service sent a fresh one because its history
 * no longer covered the revision the caller held.
 */
export const Kind = {
    /** A snapshot begins: the caller drops its view. */
    reset: "reset",
    /** One entry of the snapshot. */
    snapshot: "snapshot",
    /** The snapshot is complete: the view is whole. */
    complete: "complete",
    /** One change after the snapshot. */
    change: "change",
};
/** OneShotError is thrown when a watch subject answered with one reply. */
export class OneShotError extends Error {
    constructor(message = "watch: one reply where frames were expected") {
        super(message);
        this.name = "OneShotError";
    }
}
/**
 * Reconnected is the reason a lease is cut with when the connection
 * reconnected: the reply subject went with the connection that dropped, and
 * the lease is reopened on the next one.
 */
class Reconnected extends Error {
    constructor() {
        super("watch: the connection reconnected");
        this.name = "Reconnected";
    }
}
function parseRevision(s) {
    if (!/^\d+$/.test(s)) {
        return undefined;
    }
    const n = Number(s);
    return Number.isSafeInteger(n) ? n : undefined;
}
/**
 * open follows a leased watch for as long as the signal lives, reopening from
 * the last revision at every lease end — and at the connection's own
 * reconnect, without Since, because the reply subject the lease was answered
 * on belonged to the connection that dropped and a lease that was cut handed
 * over no revision: the fresh snapshot replaces the caller's view as any
 * fresh snapshot does (the configuration design § the leased watch; hq issue
 * 129). An error — a refusal, a gap, a stream gone idle, the signal aborting,
 * a reopen the service cannot yet answer — ends the iterator and is the
 * caller's to act on.
 */
export async function* open(nc, caller, w, decode) {
    let since = 0;
    // The lease in flight is cut at every reconnect. The listener reads the
    // connection's status for the whole watch and is stopped with it.
    let lease;
    const status = nc.status();
    void (async () => {
        for await (const st of status) {
            if (st.type === "reconnect") {
                lease?.abort(new Reconnected());
            }
        }
    })();
    try {
        for (;;) {
            const current = new AbortController();
            lease = current;
            const onAbort = () => {
                current.abort(w.signal?.reason);
            };
            if (w.signal?.aborted) {
                onAbort();
            }
            else {
                w.signal?.addEventListener("abort", onAbort, { once: true });
            }
            try {
                since = yield* follow(nc, caller, w, decode, since, current.signal);
            }
            catch (err) {
                if (!w.signal?.aborted && current.signal.reason instanceof Reconnected) {
                    // A lease cut rather than ended handed over no revision: the
                    // reopen asks for a fresh snapshot.
                    since = 0;
                    continue;
                }
                throw err;
            }
            finally {
                w.signal?.removeEventListener("abort", onAbort);
                lease = undefined;
            }
            if (w.signal?.aborted) {
                const reason = w.signal.reason;
                throw reason instanceof Error ? reason : new DOMException("watch: aborted", "AbortError");
            }
        }
    }
    finally {
        status.stop();
    }
}
/**
 * follow runs one lease: the request, with Since when the caller holds a
 * revision, and its frames as events until the terminator. It returns the
 * revision to reopen from.
 */
async function* follow(nc, caller, w, decode, since, signal) {
    const h = newHeaders();
    if (w.headers) {
        for (const [k, values] of w.headers) {
            for (const v of values) {
                h.append(k, v);
            }
        }
    }
    if (since > 0) {
        h.set(w.watchHeaders.since, String(since));
    }
    if (w.carrier) {
        h.set(CARRIER_HEADER, await w.carrier(audienceOf(w.subject)));
    }
    const r = await frame.open(nc, caller, {
        service: w.service,
        subject: w.subject,
        headers: h,
        idle: w.idle,
        signal,
    });
    if (r.oneShot) {
        r.close();
        throw new OneShotError();
    }
    let inSnapshot = false;
    for await (const f of r.frames()) {
        switch (f.kind) {
            case frame.Kind.content: {
                const snap = f.headers.get(w.watchHeaders.snapshot) === SNAPSHOT_TRUE;
                if (snap && !inSnapshot) {
                    inSnapshot = true;
                    yield { kind: Kind.reset };
                }
                const entry = decode(f.data, f.headers);
                yield { kind: snap ? Kind.snapshot : Kind.change, entry };
                break;
            }
            case frame.Kind.progress: {
                if (f.headers.get(w.watchHeaders.snapshot) !== SNAPSHOT_DONE) {
                    continue;
                }
                if (!inSnapshot) {
                    yield { kind: Kind.reset };
                }
                inSnapshot = false;
                yield { kind: Kind.complete };
                break;
            }
            case frame.Kind.terminator: {
                const rev = f.headers.get(w.watchHeaders.revision);
                if (rev !== "") {
                    const n = parseRevision(rev);
                    if (n !== undefined) {
                        since = n;
                    }
                }
                break;
            }
        }
    }
    return since;
}
//# sourceMappingURL=watch.js.map